import React, { useContext, useCallback, useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { Container, Table, Alert, Pagination, PaginationItem, PaginationLink } from "reactstrap"
import { useQuery } from "@tanstack/react-query"
import { debounce } from "lodash"

import { Loading } from "components"
import ListHeader from "ui-componets/list-header"
import { getLogs } from "services/logs-importation"
import { LogItemList } from "components/logs-importation"
import { UserContext } from "App"
import "assets/scss/custom/components/table-interactive.scss"

const LOGS_ORDERING = "-log_date,-log_time"

const LogsImportationList = () => {
  document.title = "Lista de logs | Punto de venta"

  const Location = useLocation()
  const userData = useContext(UserContext)
  const isAdmin = userData?.user_type

  const [filters, setFilters] = useState(() => {
    const initialParams = new URLSearchParams(Location.search)
    initialParams.set("ordering", LOGS_ORDERING)
    return initialParams
  })
  const [searchTxt, setSearchTxt] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [pageInput, setPageInput] = useState("1")

  const logsImportation = useQuery(["logs-importation", filters, filters.toString()], () => getLogs(filters), {
    refetchInterval: 5000,
  })

  const styleCenterTable = { textAlign: "center", verticalAlign: "middle" }

  const handleSearch = (pageParams) => {
    const params = new URLSearchParams(filters)
    params.set("ordering", LOGS_ORDERING)

    if (pageParams) {
      const newParams = new URLSearchParams(pageParams)
      newParams.set("ordering", LOGS_ORDERING)
      window.history.replaceState({}, null, `${Location.pathname}?${newParams}`)
      setFilters(newParams)
    } else if (searchTxt) {
      params.set("search", searchTxt)
      params.delete("offset")
      params.delete("per_page")
      window.history.replaceState({}, null, `${Location.pathname}?${params}`)
      setFilters(params)
    } else {
      if (params.get("search")) {
        params.delete("offset")
        params.delete("per_page")
        params.delete("search")
        window.history.replaceState({}, null, `${Location.pathname}?${params}`)
        setFilters(params)
      }
    }
  }

  const debouncedSearch = useCallback(
    debounce((text) => {
      const params = new URLSearchParams()
      params.set("ordering", LOGS_ORDERING)
      params.set("search", text)
      params.delete("offset")
      params.delete("per_page")
      window.history.replaceState({}, null, `${Location.pathname}?${params}`)
      setFilters(params)
    }, 300),
    []
  )

  function handleChangeSearch(event) {
    setSearchTxt(event.target.value)
    debouncedSearch(event.target.value)
  }

  const isLoading = logsImportation.isLoading

  const logsList = useMemo(() => {
    const rawLogsList = logsImportation?.data?.results ?? []

    // Keep newest logs first even if backend ordering is partially applied.
    return [...rawLogsList].sort((firstLog, secondLog) => {
      const firstDateTime = new Date(`${firstLog?.log_date}T${firstLog?.log_time || "00:00:00"}`)
      const secondDateTime = new Date(`${secondLog?.log_date}T${secondLog?.log_time || "00:00:00"}`)

      return secondDateTime - firstDateTime
    })
  }, [logsImportation?.data?.results])

  const paginationData = useMemo(() => {
    const params = new URLSearchParams(filters)
    const totalItems = logsImportation?.data?.count ?? logsList.length
    const perPage = Number(params.get("per_page") || logsList.length || 10)
    const offset = Number(params.get("offset") || 0)
    const totalPages = Math.max(1, Math.ceil(totalItems / perPage))
    const currentPage = Math.min(totalPages, Math.floor(offset / perPage) + 1)

    return {
      currentPage,
      perPage,
      totalItems,
      totalPages,
    }
  }, [filters, logsImportation?.data?.count, logsList.length])

  const visiblePages = useMemo(() => {
    const pages = []
    const start = Math.max(1, paginationData.currentPage - 2)
    const end = Math.min(paginationData.totalPages, paginationData.currentPage + 2)

    for (let page = start; page <= end; page += 1) {
      pages.push(page)
    }

    return pages
  }, [paginationData.currentPage, paginationData.totalPages])

  const handlePageChange = (page) => {
    if (page < 1 || page > paginationData.totalPages) {
      return
    }

    const params = new URLSearchParams(filters)
    params.set("offset", String((page - 1) * paginationData.perPage))
    params.set("per_page", String(paginationData.perPage))
    handleSearch(params.toString())
  }

  useEffect(() => {
    setPageInput(String(paginationData.currentPage))
  }, [paginationData.currentPage])

  const handlePageInputKeyDown = (event) => {
    if (event.key !== "Enter") {
      return
    }

    const targetPage = Number(pageInput)

    if (!Number.isFinite(targetPage)) {
      return
    }

    const boundedPage = Math.min(Math.max(Math.trunc(targetPage), 1), paginationData.totalPages)
    handlePageChange(boundedPage)
  }

  return !isAdmin ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Alert color="danger" isOpen={logsImportation.isError}>
            {logsImportation?.error?.errors?.map((error) => error?.message)}
          </Alert>

          <h2>Lista de Logs de Importación</h2>
          <div className="bg-white p-3">
            <ListHeader
              handleOnChangeSearch={handleChangeSearch}
              handleSearch={handleSearch}
              searchTxt={searchTxt}
              searchColMd={4}
            />
            {!logsImportation.error && isLoading ? (
              <div className="my-5 text-center">
                <Loading isLoading={isLoading} size={2} />
              </div>
            ) : logsImportation.error && !isLoading ? (
              <div className="my-5 text-center">
                <h1>Error intenta mas tarde</h1>
              </div>
            ) : (
              <div style={{ marginTop: "30px" }} className="table-responsive">
                <Table className="table-hover-effect table-interactive">
                  <thead>
                    <tr>
                      <th style={styleCenterTable}>ID</th>
                      <th style={styleCenterTable}>Fecha y hora</th>
                      <th style={styleCenterTable}>Tipo de importación</th>
                      <th style={styleCenterTable}>Estado</th>
                      <th style={styleCenterTable}>Usuario</th>
                      <th style={styleCenterTable} className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsList.length > 0 ? (
                      logsList.map((item) => {
                        return <LogItemList key={item.id} item={item} />
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center">
                          No hay datos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-3">
                  <small className="text-muted">
                    Pagina {paginationData.currentPage} de {paginationData.totalPages} - {" "}
                    {paginationData.totalItems} registros
                  </small>

                  <div className="d-flex flex-column flex-sm-row align-items-center gap-2">
                    {paginationData.totalPages > 1 && (
                      <div className="d-flex align-items-center gap-2">
                        <label htmlFor="jump-to-page" className="mb-0 text-muted small">
                          Ir a pagina
                        </label>
                        <input
                          id="jump-to-page"
                          type="number"
                          min="1"
                          max={paginationData.totalPages}
                          value={pageInput}
                          onChange={(event) => setPageInput(event.target.value)}
                          onKeyDown={handlePageInputKeyDown}
                          className="form-control form-control-sm"
                          style={{ width: "50px" }}
                        />
                      </div>
                    )}

                    {paginationData.totalPages > 1 && (
                      <Pagination aria-label="Page navigation" className="mb-0 d-flex align-items-center gap-1">
                        <PaginationItem disabled={paginationData.currentPage === 1}>
                          <PaginationLink previous onClick={() => handlePageChange(paginationData.currentPage - 1)} />
                        </PaginationItem>

                        {visiblePages.map((page) => (
                          <PaginationItem key={page} active={page === paginationData.currentPage}>
                            <PaginationLink onClick={() => handlePageChange(page)}>{page}</PaginationLink>
                          </PaginationItem>
                        ))}

                        <PaginationItem disabled={paginationData.currentPage === paginationData.totalPages}>
                          <PaginationLink next onClick={() => handlePageChange(paginationData.currentPage + 1)} />
                        </PaginationItem>
                      </Pagination>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default LogsImportationList
